import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import commonStore from '../store/CommonStore';
import tableStore from '../store/TableStore';
import userSettingsStore from '../store/UserSettingsStore';

const VIEWING_TABLES_SEARCH_PARAM = 'viewing-tables';
const VIEWING_MODAL_TABLE_SEARCH_PARAM = 'viewing-modal-table';

export const useViewingTablesPopulator = (): void => {
  const [tableIdsToClose, setTableIdsToClose] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const filteredTables = useMemo(
    () => tableStore.getFilteredTables(),
    [
      tableStore.activeTables.size,
      tableStore.filterStateChanged,
      tableStore.selectedFilterOptions,
    ],
  );

  useEffect(() => {
    if (commonStore.backgroundMode) {
      commonStore.setAllViewingTablesPopulated(false);
      return;
    }
    const viewingTableIds = searchParams
      .get(VIEWING_TABLES_SEARCH_PARAM)
      ?.split(',')
      ?.reverse();

    const viewingModalTableId = searchParams.get(
      VIEWING_MODAL_TABLE_SEARCH_PARAM,
    );

    commonStore.setAllViewingTablesPopulated(false);
    commonStore.enableLoading();
    tableStore
      .initializeTables(viewingTableIds, viewingModalTableId, () =>
        commonStore.setAllViewingTablesPopulated(true),
      )
      .finally(commonStore.disableLoading);
  }, [commonStore.backgroundMode]);

  useEffect(() => {
    if (!commonStore.isAllViewingTablesPopulated) {
      return;
    }
    if (tableStore.viewingTableIds.length) {
      searchParams.set(
        VIEWING_TABLES_SEARCH_PARAM,
        tableStore.viewingTableIds.join(','),
      );
    } else {
      searchParams.delete(VIEWING_TABLES_SEARCH_PARAM);
    }
    setSearchParams(searchParams);
  }, [
    tableStore.viewingTableIds,
    tableStore.viewingTableIds.length,
    commonStore.isAllViewingTablesPopulated,
  ]);

  useEffect(() => {
    if (tableStore.viewingModalWindowTableId) {
      searchParams.set(
        VIEWING_MODAL_TABLE_SEARCH_PARAM,
        tableStore.viewingModalWindowTableId,
      );
    } else {
      searchParams.delete(VIEWING_MODAL_TABLE_SEARCH_PARAM);
    }
    setSearchParams(searchParams);
  }, [tableStore.viewingModalWindowTableId]);

  useEffect(() => {
    if (
      tableStore.selectedFilterOptions.autoOpenTables &&
      commonStore.isAllViewingTablesPopulated
    ) {
      const filteredTableIds = filteredTables.map(table => table.id);

      const notOpenedTableIds = _.difference(
        filteredTableIds,
        tableStore.viewingTableIds,
      );
      if (notOpenedTableIds.length) {
        commonStore.setAllViewingTablesPopulated(false);
        tableStore
          .addViewingTableIds(notOpenedTableIds, () =>
            commonStore.setAllViewingTablesPopulated(true),
          )
          .then();
      }
    }
  }, [
    filteredTables,
    tableStore.selectedFilterOptions.autoOpenTables,
    tableStore.viewingTableIds.length,
    commonStore.isAllViewingTablesPopulated,
  ]);

  useEffect(() => {
    if (
      tableStore.selectedFilterOptions.autoCloseTables &&
      commonStore.isAllViewingTablesPopulated
    ) {
      const filteredTableIds = filteredTables.map(table => table.id);

      const notClosedTableIds = _.difference(
        tableStore.viewingTableIds,
        filteredTableIds,
        tableIdsToClose,
      );

      if (notClosedTableIds.length) {
        setTableIdsToClose(prevState => [...prevState, ...notClosedTableIds]);
        setTimeout(
          () =>
            notClosedTableIds.forEach(tableId => {
              const table = tableStore.getActiveTableById(tableId);
              if (table && !tableStore.isTableSatisfyFilter(table)) {
                tableStore.removeViewingTable(tableId);
              }
            }),
          userSettingsStore.settings.table.autoCloseTables,
        );
      }
    }
  }, [
    filteredTables,
    tableStore.selectedFilterOptions,
    tableStore.viewingTableIds.length,
    commonStore.isAllViewingTablesPopulated,
    userSettingsStore.settings.table.autoCloseTables,
    tableStore.activeTables.size,
  ]);

  useEffect(() => {
    setTableIdsToClose(prevState =>
      prevState.filter(t => tableStore.viewingTableIds.includes(t)),
    );
  }, [tableStore.viewingTableIds.length]);
};
